import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getFighters, compareFighters, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { 
  Users,
  Scale,
  Swords,
  Trophy,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

const StatBar = ({ label, value1, value2, maxValue = 100 }) => {
  const diff = value1 - value2;
  const winner = diff > 0 ? 1 : diff < 0 ? 2 : 0;
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className={cn(
          "font-heading text-2xl tabular-nums",
          winner === 1 ? "text-green-500" : "text-white"
        )}>
          {value1}
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">{label}</span>
        <span className={cn(
          "font-heading text-2xl tabular-nums",
          winner === 2 ? "text-green-500" : "text-white"
        )}>
          {value2}
        </span>
      </div>
      <div className="flex gap-2 items-center">
        <div className="flex-1 h-3 bg-ba-border rounded-full overflow-hidden flex justify-end">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${(value1 / maxValue) * 100}%` }}
            transition={{ duration: 0.5 }}
            className={cn(
              "h-full rounded-full",
              winner === 1 ? "bg-green-500" : "bg-red-600"
            )}
          />
        </div>
        <div className="w-8 flex justify-center">
          {winner === 1 ? <TrendingUp className="w-4 h-4 text-green-500" /> :
           winner === 2 ? <TrendingDown className="w-4 h-4 text-red-500" /> :
           <Minus className="w-4 h-4 text-zinc-500" />}
        </div>
        <div className="flex-1 h-3 bg-ba-border rounded-full overflow-hidden">
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: `${(value2 / maxValue) * 100}%` }}
            transition={{ duration: 0.5 }}
            className={cn(
              "h-full rounded-full",
              winner === 2 ? "bg-green-500" : "bg-red-600"
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const FighterComparisonPage = () => {
  const { isAdmin } = useAuth();
  const [fighters, setFighters] = useState([]);
  const [fighter1Id, setFighter1Id] = useState('');
  const [fighter2Id, setFighter2Id] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fightersLoading, setFightersLoading] = useState(true);

  useEffect(() => {
    loadFighters();
    trackFeatureUsage('fighter_comparison', 'view').catch(() => {});
  }, []);

  const loadFighters = async () => {
    try {
      const response = await getFighters();
      setFighters(response.data);
    } catch (error) {
      toast.error('Failed to load fighters');
    } finally {
      setFightersLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!fighter1Id || !fighter2Id) {
      toast.error('Please select two fighters to compare');
      return;
    }
    if (fighter1Id === fighter2Id) {
      toast.error('Please select two different fighters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await compareFighters(fighter1Id, fighter2Id);
      setComparison(response.data);
      trackFeatureUsage('fighter_comparison', 'compare').catch(() => {});
    } catch (error) {
      toast.error('Failed to compare fighters');
    } finally {
      setLoading(false);
    }
  };

  const f1 = comparison?.fighter1;
  const f2 = comparison?.fighter2;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fighter-comparison-page">
        {/* Header */}
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Analysis Tool</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
            FIGHTER COMPARISON
          </h1>
          <p className="text-zinc-500 mt-2 font-body">Compare two fighters side by side</p>
        </div>

        {/* Fighter Selection */}
        <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Fighter 1</label>
              <Select value={fighter1Id} onValueChange={setFighter1Id}>
                <SelectTrigger className="h-12 bg-ba-black border-ba-border" data-testid="fighter1-select">
                  <SelectValue placeholder="Select fighter..." />
                </SelectTrigger>
                <SelectContent>
                  {fighters.map(f => (
                    <SelectItem key={f.id} value={f.id} disabled={f.id === fighter2Id}>
                      {f.name} ({f.weight_class})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-center">
              <div className="w-12 h-12 bg-red-600/20 border border-red-600/50 rounded-full flex items-center justify-center">
                <Swords className="w-6 h-6 text-red-500" />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Fighter 2</label>
              <Select value={fighter2Id} onValueChange={setFighter2Id}>
                <SelectTrigger className="h-12 bg-ba-black border-ba-border" data-testid="fighter2-select">
                  <SelectValue placeholder="Select fighter..." />
                </SelectTrigger>
                <SelectContent>
                  {fighters.map(f => (
                    <SelectItem key={f.id} value={f.id} disabled={f.id === fighter1Id}>
                      {f.name} ({f.weight_class})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-center mt-6">
            <Button
              onClick={handleCompare}
              disabled={!fighter1Id || !fighter2Id || loading}
              className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider px-8 transition-all"
              data-testid="compare-btn"
            >
              {loading ? 'Comparing...' : 'Compare Fighters'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Comparison Results */}
        {comparison && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Fighter Names Header */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-ba-surface border border-ba-border p-4 rounded-sm">
                <p className="font-heading text-3xl text-white">{f1?.info?.name?.toUpperCase()}</p>
                <p className="text-zinc-500 font-body text-sm">{f1?.info?.weight_class}</p>
              </div>
              <div className="flex items-center justify-center">
                <span className="font-heading text-4xl text-red-600">VS</span>
              </div>
              <div className="bg-ba-surface border border-ba-border p-4 rounded-sm">
                <p className="font-heading text-3xl text-white">{f2?.info?.name?.toUpperCase()}</p>
                <p className="text-zinc-500 font-body text-sm">{f2?.info?.weight_class}</p>
              </div>
            </div>

            {/* Assessment Scores */}
            <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
              <h3 className="font-heading text-xl text-white mb-6 text-center">ASSESSMENT SCORES</h3>
              <div className="space-y-6">
                <StatBar 
                  label="Striking" 
                  value1={f1?.assessment?.striking || 0} 
                  value2={f2?.assessment?.striking || 0} 
                />
                <StatBar 
                  label="Grappling" 
                  value1={f1?.assessment?.grappling || 0} 
                  value2={f2?.assessment?.grappling || 0} 
                />
                <StatBar 
                  label="Wrestling" 
                  value1={f1?.assessment?.wrestling || 0} 
                  value2={f2?.assessment?.wrestling || 0} 
                />
                <StatBar 
                  label="Cardio" 
                  value1={f1?.assessment?.cardio || 0} 
                  value2={f2?.assessment?.cardio || 0} 
                />
              </div>
            </div>

            {/* Fight Records */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading text-xl text-white">FIGHT RECORD</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-green-500">{f1?.fight_stats?.wins || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Wins</p>
                  </div>
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-green-500">{f2?.fight_stats?.wins || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Wins</p>
                  </div>
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-red-500">{f1?.fight_stats?.losses || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Losses</p>
                  </div>
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-red-500">{f2?.fight_stats?.losses || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Losses</p>
                  </div>
                </div>
              </div>

              <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Swords className="w-5 h-5 text-red-500" />
                  <h3 className="font-heading text-xl text-white">FINISH RATE</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-yellow-500">{f1?.fight_stats?.kos || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">KO/TKOs</p>
                  </div>
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-yellow-500">{f2?.fight_stats?.kos || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">KO/TKOs</p>
                  </div>
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-purple-500">{f1?.fight_stats?.submissions || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Submissions</p>
                  </div>
                  <div className="text-center p-4 bg-ba-black rounded-sm">
                    <p className="font-heading text-4xl text-purple-500">{f2?.fight_stats?.submissions || 0}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">Submissions</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Weight History */}
            {(f1?.weight_history?.length > 0 || f2?.weight_history?.length > 0) && (
              <div className="bg-ba-surface border border-ba-border p-6 rounded-sm">
                <div className="flex items-center gap-2 mb-4">
                  <Scale className="w-5 h-5 text-blue-500" />
                  <h3 className="font-heading text-xl text-white">RECENT WEIGHTS</h3>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-zinc-500 font-body text-sm mb-2">{f1?.info?.name}</p>
                    {f1?.weight_history?.slice(0, 5).map((w, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-ba-border">
                        <span className="text-zinc-400 text-sm">{w.date?.slice(0, 10)}</span>
                        <span className="text-white font-mono">{w.weight} lbs</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-zinc-500 font-body text-sm mb-2">{f2?.info?.name}</p>
                    {f2?.weight_history?.slice(0, 5).map((w, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-ba-border">
                        <span className="text-zinc-400 text-sm">{w.date?.slice(0, 10)}</span>
                        <span className="text-white font-mono">{w.weight} lbs</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Empty State */}
        {!comparison && !loading && (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500 font-body">Select two fighters above to compare them</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FighterComparisonPage;
